import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import Scrollbar from '../../../components/scrollbar/Scrollbar';
import { StockAdjustmentService } from '../../../api/stock-adjustment.service';
import { fDateTime } from '../../../utils/formatTime';
import { useAuthContext } from '../../../auth/useAuthContext';
import { formatLabel } from '../../../utils/formatLabel';

type Props = {
  open: boolean;
  onClose: VoidFunction;
};

type Adjustment = {
  id: string;
  qtyAdjusted: number;
  reason: string;
  notes: string;
  status: string;
  createdDate: string;
  product: {
    name: string;
    code: string;
  };
  createdByUser: {
    firstName: string;
    lastName: string;
  };
};

export default function StockAdjustmentRequestsDialog({
  open,
  onClose,
}: Props) {
  const { user } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMyRequests = async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const service = new StockAdjustmentService();
      // Fetch adjustments created by me (no status filter to show all)
      const { data } = await service.findAll({
        createdBy: String(user.id),
        limit: 100,
      });
      setAdjustments(data.data);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to fetch your requests', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMyRequests();
    }
  }, [open, user]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>My Adjustment Requests</DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Scrollbar sx={{ maxHeight: 600 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell align="center">Qty Change</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adjustments.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{fDateTime(row.createdDate)}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {row.product?.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary' }}
                      >
                        {row.product?.code}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color:
                            row.qtyAdjusted > 0 ? 'success.main' : 'error.main',
                        }}
                      >
                        {row.qtyAdjusted > 0 ? '+' : ''}
                        {row.qtyAdjusted}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatLabel(row.reason)}</TableCell>
                    <TableCell>
                      <Chip
                        label={formatLabel(row.status)}
                        color={getStatusColor(row.status) as any}
                        size="small"
                        variant="soft"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap>
                        {row.notes || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && adjustments.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        No requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
